/**
 * Header.tsx — Audit project header: title + status + a 4-cell stat bar
 * (Sede, Superficie, Valore CME, Costo/m² — all real fields; the prototype's
 * Logistica/Categoria cells had no backing data and were removed 2026-07-24).
 * Ported from prototype stage-audit.jsx `Header`. Display-only: per the
 * 2026-07-23 UX decision the analysis has NO manual button here — it auto-
 * starts at project creation and its state lives in the table's empty-state
 * area (AuditPage/UnifiedTable) + the upload progress page.
 */
import { I, type IconComponent } from "../../components/icons";
import { type TenderProject, unreadAmountsWords } from "../../lib/tenderApi";
import { fmtEur } from "./helpers";

function Stat({
  icon: Ico,
  label,
  value,
  sub,
  bold,
  last,
}: {
  icon: IconComponent;
  label: string;
  value: string;
  sub?: string;
  bold?: boolean;
  last?: boolean;
}): JSX.Element {
  return (
    <div style={{ padding: "14px 18px", borderRight: last ? "none" : "1px solid var(--c-line)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--c-ink-3)",
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        <Ico size={11} /> {label}
      </div>
      <div
        style={{
          fontSize: bold ? 17 : 14,
          fontWeight: bold ? 600 : 500,
          marginTop: 4,
          fontFamily: bold ? "var(--font-mono)" : "inherit",
          letterSpacing: bold ? "-0.01em" : 0,
        }}
      >
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function Header({
  project,
  loading,
}: {
  project: TenderProject | undefined;
  loading: boolean;
}): JSX.Element {
  const name = project?.name ?? (loading ? "Caricamento…" : "Nessun progetto");
  const created = project
    ? new Date(project.createdAt).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
  const surface = project?.surfaceM2 ?? null;
  const total = project?.totalValue ?? null;
  // Goal 55B, L-1: the total is the sum of the amounts READ. With unread amounts it is said to
  // be only that, and with every amount unread it is «non determinabile» — never € 0, never
  // «—» (which reads «not analysed yet»). A €/m² of an incomplete total is not determinable.
  const unread = project?.unreadAmounts ?? 0;
  const valore =
    unread && total == null ? "non determinabile" : total != null ? fmtEur(total) : "—";
  const valoreSub = !unread
    ? undefined
    : total == null
      ? unreadAmountsWords(unread)
      : `solo importi letti · ${unreadAmountsWords(unread)}`;
  // €/m² is derivable from the CME total + surface; "—" until both exist.
  const perM2 = total != null && surface && !unread ? Math.round(total / surface) : null;

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 22 }}>
      <div
        style={{
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          borderBottom: "1px solid var(--c-line)",
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 8,
            background: "var(--c-brand-soft)",
            color: "var(--c-brand-ink)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <I.Building size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0, letterSpacing: "-0.01em" }}>
              {name}
            </h1>
            {project && (
              <span className="pill pill-warn">
                <I.Clock size={10} />
                {project.statusLabel}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginTop: 3 }}>
            {project ? `Creato il ${created}` : "Apri un progetto dall'Ingest"}
          </div>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          borderTop: "1px solid var(--c-line)",
        }}
      >
        <Stat icon={I.Pin} label="Sede" value={project?.city ?? "—"} />
        <Stat
          icon={I.Ruler}
          label="Superficie"
          value={surface != null ? `${surface.toLocaleString("it-IT")} m²` : "—"}
        />
        <Stat icon={I.Euro} label="Valore CME" value={valore} sub={valoreSub} bold />
        <Stat
          icon={I.Layers}
          label="Costo / m²"
          value={
            perM2 != null
              ? `€ ${perM2.toLocaleString("it-IT")}`
              : unread && surface
                ? "non determinabile"
                : "—"
          }
          bold
          last
        />
      </div>
    </div>
  );
}
